import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { getDownloadURL, ref } from 'firebase/storage'
import {
  isInCycle,
  type StatementCycle,
  type Transaction,
} from '@budgetmonkey/shared'
import { useCurrentUser } from '@/auth/AuthProvider'
import { storage } from '@/lib/firebase'
import {
  createPeriod,
  deletePeriod,
  fetchPeriods,
  fetchStatement,
  generatePacket,
  ingestStatement,
  linkStatementLine,
  uploadStatementPages,
  markSubmitted,
  setPeriodItems,
} from './api'

const periodsKey = (userId: string) => ['reconciliationPeriods', userId] as const

export function usePeriods() {
  const user = useCurrentUser()
  return useQuery({
    queryKey: periodsKey(user.uid),
    queryFn: () => fetchPeriods(user.uid),
  })
}

/**
 * The P-Card charges that belong on a cycle's packet — §5.3.
 *
 * P-Card only. The other sources are confirmed by a CSIU import, never by a
 * packet, so putting them on one would ask the business office to sign for
 * charges that reconcile somewhere else entirely.
 *
 * Voided charges are excluded: they are in the ledger for the audit trail but
 * count toward nothing, and a packet that lists one would not agree with the
 * statement.
 *
 * Ordered by purchase date as a starting point. Statement order supersedes
 * this once a statement is ingested — §5.5 — but a date-ordered packet is
 * still submittable, which is the point of having it now.
 */
export function packetCandidates(
  transactions: readonly Transaction[],
  cycle: StatementCycle,
): Transaction[] {
  return transactions
    .filter((t) => t.source === 'pcard')
    .filter((t) => t.status !== 'void')
    .filter((t) => isInCycle(t.transactionDate, cycle))
    .sort((a, b) => a.transactionDate.localeCompare(b.transactionDate))
}

/**
 * Upload a photographed statement and read it — the front door to a packet.
 *
 * The statement decides which charges appear, in what order, and under which
 * vendor spelling (§5.5). Everything else on this screen is a correction to
 * what it produced.
 */
export function useIngestStatement() {
  const user = useCurrentUser()
  const client = useQueryClient()
  const [progress, setProgress] = useState<{ done: number; total: number }>()

  const mutation = useMutation({
    mutationFn: async (files: readonly File[]) => {
      setProgress({ done: 0, total: files.length })

      // Both stages are named in their errors. "Permission denied" alone does
      // not say whether the pages failed to upload or the reading of them
      // failed, and those are repaired in completely different places.
      let paths: string[]
      try {
        paths = await uploadStatementPages(user.uid, files, (done, total) =>
          setProgress({ done, total }),
        )
      } catch (cause) {
        throw new Error(
          `Couldn't upload the statement pages: ${describe(cause)}`,
          { cause },
        )
      }

      // Uploads are done; reading takes appreciably longer, so the caller
      // shows a different message rather than a stalled progress count.
      setProgress(undefined)

      try {
        return await ingestStatement(paths)
      } catch (cause) {
        throw new Error(
          `The pages uploaded, but reading them failed: ${describe(cause)}`,
          { cause },
        )
      }
    },
    onSuccess: () => client.invalidateQueries({ queryKey: periodsKey(user.uid) }),
    onError: () => setProgress(undefined),
  })

  return { ...mutation, progress }
}

/** The statement behind a period, if one was ever ingested for it. */
export function useStatement(statementId: string | null) {
  return useQuery({
    queryKey: ['creditCardStatement', statementId],
    queryFn: () => fetchStatement(statementId!),
    enabled: statementId !== null,
  })
}

/**
 * Attach a captured transaction to the statement line it pays for.
 *
 * Invalidates both: the line's match state lives on the statement, the packet
 * order it re-derives lives on the period, and a screen showing one refreshed
 * and the other stale is worse than showing neither.
 */
export function useLinkStatementLine(statementId: string, periodId: string) {
  const user = useCurrentUser()
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({
      lineIndex,
      transactionId,
    }: {
      lineIndex: number
      transactionId: string | null
    }) => linkStatementLine(statementId, periodId, lineIndex, transactionId),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: ['creditCardStatement', statementId] }),
        client.invalidateQueries({ queryKey: periodsKey(user.uid) }),
      ])
    },
  })
}

export function useCreatePeriod() {
  const user = useCurrentUser()
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({
      cycle,
      transactionIds,
    }: {
      cycle: StatementCycle
      transactionIds: readonly string[]
    }) => createPeriod(user.uid, cycle, transactionIds),
    onSuccess: () => client.invalidateQueries({ queryKey: periodsKey(user.uid) }),
  })
}

export function useSetPeriodItems() {
  const user = useCurrentUser()
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ periodId, ids }: { periodId: string; ids: readonly string[] }) =>
      setPeriodItems(periodId, ids),
    onSuccess: () => client.invalidateQueries({ queryKey: periodsKey(user.uid) }),
  })
}

export function useGeneratePacket() {
  const user = useCurrentUser()
  const client = useQueryClient()
  return useMutation({
    mutationFn: (periodId: string) => generatePacket(periodId),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: periodsKey(user.uid) }),
        // Generating overwrote the packet file, which revoked the token in
        // every URL already handed out for it (see `usePacketUrl`). The path is
        // unchanged, so the cached URL still looks right and now 403s in a new
        // tab. Dropped by prefix: both the PDF and the XLSX are affected, and
        // the second generate is the one that breaks — the first had no URL to
        // invalidate, which is why this survived a working packet on 8 August.
        client.invalidateQueries({ queryKey: [PACKET_URL_KEY] }),
      ])
    },
  })
}

export function useDeletePeriod() {
  const user = useCurrentUser()
  const client = useQueryClient()
  return useMutation({
    mutationFn: (periodId: string) => deletePeriod(periodId),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: periodsKey(user.uid) }),
        // The statement's vendor stamp came off the transactions, so the
        // ledger the packet screen reads has changed too.
        client.invalidateQueries({ queryKey: ['transactions', user.uid] }),
      ])
    },
  })
}

/**
 * Submitting confirms every charge on the packet, so the ledger changes too —
 * refreshing only the period would leave every badge reading "Unconfirmed"
 * until something else happened to invalidate it, which is how the missing
 * confirmation stayed invisible in the first place.
 */
export function useMarkSubmitted() {
  const user = useCurrentUser()
  const client = useQueryClient()
  return useMutation({
    mutationFn: (periodId: string) => markSubmitted(periodId),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: periodsKey(user.uid) }),
        client.invalidateQueries({ queryKey: ['transactions', user.uid] }),
        client.invalidateQueries({ queryKey: ['transactionsByIds'] }),
      ])
    },
  })
}

/** Whatever a thrown value actually is, said in one line. */
function describe(cause: unknown): string {
  if (cause instanceof Error) return cause.message
  return String(cause)
}

/** Cache key for a packet's download URL. Regeneration must invalidate these. */
export const PACKET_URL_KEY = 'packetUrl'

/**
 * A download URL for a generated packet, fetched only when one exists.
 *
 * The URL carries a token, not the caller's credentials — that is what lets it
 * open in a new tab, where there is no signed-in session to check. The token
 * lives in the object's metadata, and the generator writes the packet with the
 * Admin SDK, replacing that metadata every time. So regenerating a packet
 * revokes every URL already issued for it.
 *
 * Nothing about the path changes when that happens: same period, same file. A
 * cached URL therefore looks perfectly current and is dead, and the failure
 * arrives as a raw `403 Permission denied` in a new tab — Storage refusing an
 * unauthenticated request whose token no longer matches. Which reads like a
 * rules problem and is not one.
 *
 * `useGeneratePacket` drops these on success, which covers the tab that
 * pressed the button. It cannot reach a second device with the screen already
 * open, so this deliberately keeps the app's ordinary 30-second staleness
 * rather than the half-hour it used to hold: minting a URL is one small
 * request, and returning to the tab is enough to heal a revoked one.
 */
export function usePacketUrl(storagePath: string | null) {
  return useQuery({
    queryKey: [PACKET_URL_KEY, storagePath],
    queryFn: () => getDownloadURL(ref(storage, storagePath!)),
    enabled: storagePath !== null,
  })
}
